import { State, process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Drawer, DrawerContent } from "@progress/kendo-react-layout";
import { Plus, X } from "lucide-react";
import { createRef, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ShowColumnsFilter } from "../../../component/global/ColumnMenu";
import Export from "../../../component/global/Export";
import CustomLabel from "../../../component/global/RequiredMark";
import BBSTextField from "../../../component/inputs/BBSTextField";
import PageTitle from "../../../component/ui/PageTitle";
import SearchBox from "../../../component/ui/SearchBox";
import Filters from "../../../constants/filters";
import { IBarType, IStandard } from "../../../interfaces/standard.interface";
import { ActionCellPopupSettings } from "../../../shared-components/custom-cells";
import { setToast } from "../../../store/features/toastSlice";
import {
  MIN_RESIZABLE_WIDTH_KENDO_TABLE,
  getPaginationData,
} from "../../../utils/utils";

const Pagination = getPaginationData();
let mappingChanged = false;

interface IBarTypeWithIds extends IBarType {
  idx: number;
}
interface IProps {
  standards: IStandard[];
  barTypes: IBarTypeWithIds[];
  total: number;
  handleValidation: (name: string, isValid: boolean) => void;
  handleCreate: any;
  handleEdit: any;
  handleDelete: any;
  setBarTypes: any;
  action: string;
}

function BarTypes({
  standards,
  barTypes,
  setBarTypes,
  total,
  handleValidation,
  handleCreate,
  handleEdit,
  handleDelete,
  action,
}: IProps) {
  const _exporter = createRef<ExcelExport>();
  const excelExport = () => {
    if (_exporter.current) {
      _exporter.current?.save();
    }
  };

  const initialRender = useRef(false);
  const dispatch = useDispatch();

  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [oldBarTypes, setOldBarTypes] = useState<IBarType[]>([]);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(
      action,
      handleEditClick,
      deleteBarType,
      onColumnsSubmit,
      null,
      columnsMapping,
      mappingChanged
    )
  );
  const [addNewBarTypes, setAddNewBarTypes] = useState<{
    name: string;
    code: string;
    standardId: string;
  }>();
  const [editBarTypeValues, setEditBarTypeValues] = useState<
    IBarType & { standardId: string; idx: number; standardName: string }
  >();

  const createDataState = (dataState: State, data: any) => {
    let createdState: any = {
      result: [],
      dataState: dataState,
    };
    if (data?.data) {
      const temp = data.data;
      createdState = {
        result: process(temp.slice(0), dataState),
        dataState: dataState,
      };
    } else if (data) {
      createdState = {
        result: process(data?.slice(0), dataState),
        dataState: dataState,
      };
    }
    return createdState;
  };

  let initialState = createDataState(
    {
      take: 20,
      skip: 0,
    },
    barTypes
  );

  const [dataState, setDataState] = useState<State>(initialState.dataState);

  function handleEditClick(e: any) {
    setShowEditDrawer(true);
    setEditBarTypeValues(e);
  }

  const handleValueChange = (e: any) => {
    setAddNewBarTypes((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEditChange = (e: any) => {
    setEditBarTypeValues((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      action,
      handleEditClick,
      deleteBarType,
      onColumnsSubmit,
      columnsState,
      columnsMapping,
      mappingChanged
    );
    setStateColumns(cols);
  }

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    if (event.dataState.filter === undefined) {
      createDataState(
        {
          take: 20,
          skip: 0,
        },
        barTypes
      );
      setBarTypes(oldBarTypes);
      setDataState(initialState.dataState);
      return;
    }
    let updatedState = createDataState(event.dataState, barTypes);
    setBarTypes(updatedState.result);
    setDataState(updatedState.dataState);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value?.length !== 1) {
      const filteredData = oldBarTypes.filter(
        (barType) =>
          barType.name.toLowerCase().includes(value.toLowerCase()) ||
          barType.code.toLowerCase().includes(value.toLowerCase())
      );
      setBarTypes(filteredData);
    }
  };

  useEffect(() => {
    if (!barTypes?.length || !columnsMapping) return;
    const cols = generateColumns(
      action,
      handleEditClick,
      deleteBarType,
      onColumnsSubmit,
      null,
      columnsMapping,
      mappingChanged
    );
    setStateColumns(cols);
  }, [barTypes, columnsMapping]);

  const createBarType = () => {
    for (let barType of barTypes) {
      if (barType.code === addNewBarTypes?.code) {
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: "Code must be unique",
          })
        );
        return;
      }

      if (barType.name === addNewBarTypes?.name) {
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: "Name must be unique",
          })
        );
        return;
      }
    }
    if (addNewBarTypes) {
      handleCreate(addNewBarTypes, "barTypes");
      setShowCreateDrawer(false);
    }
  };

  const editBarType = () => {
    for (let barType of barTypes) {
      if (barType.code === editBarTypeValues?.code) {
        if (barType.idx !== editBarTypeValues?.idx) {
          dispatch(
            setToast({
              toastType: "error",
              toastMsg: "Code must be unique",
            })
          );
          return;
        }
      }

      if (barType.name === editBarTypeValues?.name) {
        if (barType.idx !== editBarTypeValues?.idx) {
          dispatch(
            setToast({
              toastType: "error",
              toastMsg: "Name must be unique",
            })
          );
          return;
        }
      }
    }
    handleEdit(editBarTypeValues, "barTypes");
    setShowEditDrawer(false);
  };

  function deleteBarType(e: any) {
    handleDelete(e, "barTypes");
  }

  const getDropdownValue = () => {
    const standard = standards?.find(
      (std) => std.id === editBarTypeValues?.standardId
    );
    if (!standard) return;
    return standard.name;
  };

  useEffect(() => {
    if (barTypes?.length && initialRender.current === false) {
      setOldBarTypes(barTypes);
      initialRender.current = true;
    }
  }, [barTypes]);
  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={barTypes}
          {...dataState}
          onDataStateChange={dataStateChange}
          sortable={true}
          pageable={true}
          pageSize={20}
          total={total}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              {action !== "read" && (
                <Button
                  themeColor={"primary"}
                  className="border border-[#E2E8F0]"
                  onClick={() => setShowCreateDrawer(true)}
                >
                  <span className="inline-flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add New
                  </span>
                </Button>
              )}
              <div
                className="cursor-pointer  sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={oldBarTypes}
                  collapsible={true}
                  fileName="BarTypes.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      // width={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    // width={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      {/*Create Drawer*/}
      <Drawer
        expanded={showCreateDrawer}
        position={"start"}
        mode={"overlay"}
        animation={{ duration: 400 }}
        className="customDrawer2 transition-all duration-500"
      >
        <DrawerContent>
          <div
            className="overlay"
            onClick={() => setShowCreateDrawer(false)}
          ></div>
          <div
            className={`max-w-[400px] flex flex-col bg-neutral-10 px-4 fixed right-0 top-0 bottom-0 h-full w-full transform transition-all duration-500 ${
              showCreateDrawer ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="py-4 flex items-center justify-between gap-2 border-b border-neutral-30">
              <h2 className="font-medium text-lg">Add New Bar Type</h2>
              <Button
                fillMode="flat"
                className="p-2 bg-none outline-none"
                onClick={() => setShowCreateDrawer(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 py-4">
              <div>
                <BBSTextField
                  name="code"
                  value={addNewBarTypes?.code}
                  onChange={handleValueChange}
                  onValidate={handleValidation}
                  label="Code"
                  required={true}
                  placeholder="Enter Code"
                  disabled={false}
                  regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
                  error="Invalid Code"
                />
              </div>
              <div className="grid gap-4">
                <div>
                  <BBSTextField
                    name="name"
                    value={addNewBarTypes?.name}
                    onChange={handleValueChange}
                    onValidate={handleValidation}
                    label="Name"
                    required={true}
                    placeholder="Enter Name"
                    disabled={false}
                    regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
                    error="Invalid Name"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pb-4">
              <Button
                className="border border-[#E2E8F0] px-4 py-2 font-medium"
                onClick={() => setShowCreateDrawer(false)}
              >
                Cancel
              </Button>
              <Button
                className="border border-[#E2E8F0] px-4 py-2 font-medium"
                themeColor={"primary"}
                onClick={createBarType}
                disabled={!addNewBarTypes?.name}
              >
                Save
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      {/*Edit Drawer*/}
      <Drawer
        expanded={showEditDrawer}
        position={"start"}
        mode={"overlay"}
        animation={{ duration: 400 }}
        className="customDrawer2 transition-all duration-500"
      >
        <DrawerContent>
          <div
            className="overlay"
            onClick={() => setShowEditDrawer(false)}
          ></div>
          <div
            className={`max-w-[400px] flex flex-col bg-neutral-10 px-4 fixed right-0 top-0 bottom-0 h-full w-full transform transition-all duration-500 ${
              showEditDrawer ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="py-4 flex items-center justify-between gap-2 border-b border-neutral-30">
              <h2 className="font-medium text-lg">Edit Standard</h2>
              <Button
                fillMode="flat"
                className="p-2 bg-none outline-none"
                onClick={() => setShowEditDrawer(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 py-4">
              <div className="grid gap-4">
                <div>
                  <BBSTextField
                    name="code"
                    value={editBarTypeValues?.code}
                    onChange={handleEditChange}
                    onValidate={handleValidation}
                    label="Code"
                    required={true}
                    placeholder="Enter Code"
                    disabled={false}
                    regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
                    error="Invalid Code"
                  />
                </div>
                <div>
                  <BBSTextField
                    name="name"
                    value={editBarTypeValues?.name}
                    onChange={handleEditChange}
                    onValidate={handleValidation}
                    label="Name"
                    required={true}
                    placeholder="Enter Name"
                    disabled={false}
                    regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
                    error="Invalid Name"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pb-4">
              <Button
                className="border border-[#E2E8F0] px-4 py-2 font-medium"
                onClick={() => setShowEditDrawer(false)}
              >
                Cancel
              </Button>
              <Button
                className="border border-[#E2E8F0] px-4 py-2 font-medium"
                themeColor={"primary"}
                onClick={editBarType}
                disabled={!editBarTypeValues?.name}
              >
                Save
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default BarTypes;

export interface columnInterface {
  title?: string;
  field?: string;
  show?: boolean;
  filter?: "boolean" | "numeric" | "text" | "date" | undefined;
  minWidth?: number;
  minGridWidth?: number;
  locked?: boolean;
  width?: string | number;
  columnMenu?: any;
  minResizableWidth?: number;
  cells?: any;
}

export const generateColumns = (
  action: string,
  handleEditClick: any,
  handleDeleteClick: any,
  onColumnsSubmit: any,
  columnsExternal?: any,
  columnsMapping?: any,
  changed?: boolean
): any => {
  const isVisible = (field: string) => {
    if (!columnsMapping?.length) return false;
    if (columnsMapping.includes(field)) {
      return true;
    }

    return false;
  };
  const columns = [
    {
      title: "Name",
      field: "name",
      show: true,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={columns || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
          disabledColumns={["name"]}
        />
      ),
      cells: {
        data: (props: any) => (
          <ActionCellPopupSettings
            rowData={props.dataItem}
            handleEditClick={handleEditClick}
            handleDeleteClick={handleDeleteClick}
            dataKey="name"
            action={action}
          />
        ),
      },
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
    },
    {
      title: "Code",
      field: "code",
      show: changed ? isVisible("code") : true,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={columns || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
          disabledColumns={["name"]}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
    },
  ];

  return columns;
};
